/**
 * §P.3 · Los descuentos.
 *
 * ## La excepción de los céntimos
 *
 * Todo el dinero del proyecto va en **céntimos enteros**. Los descuentos son la
 * única excepción, y no por gusto: `value` es un `numeric(7,2)` que comparte
 * columna con el porcentaje, así que un descuento `fixed` de 5 € llega como
 * `value: 5` y **no** como `500`.
 *
 * Es exactamente el tipo de cosa que se pasa por alto y acaba en un descuento cien
 * veces mayor del pactado. Por eso `aValorDeRegla` está aquí y no reusa
 * `aCentimos`: son dos unidades distintas y mezclarlas es el fallo.
 *
 * ## Por qué los descuentos no son un precio pactado
 *
 * `BFF_BO.md` §7.3 y `endpoints.md` §P.3 lo dicen igual: **se acumulan**. Un
 * segundo hijo que además entró con la promo de septiembre tiene los dos.
 * Apañarlo con `custom_amount_cents` funciona y **pierde el porqué**: queda un
 * 26,50 € a pelo que nadie sabe de dónde sale y que nadie se acuerda de volver a
 * subir. Eso es fuga de ingresos del club, silenciosa y permanente.
 */

/** `percent` es un porcentaje; `fixed` son **euros**. Ver la cabecera. */
export type TipoDeDescuento = 'percent' | 'fixed';

export type AmbitoDeDescuento =
  'manual' | 'sibling' | 'family' | 'annual_prepay' | 'founder';

export interface ReglaDeDescuento {
  readonly id: string;
  /** La etiqueta corta: `HERMANOS`, `SEPT26`. **No se edita.** */
  readonly code: string;
  readonly name: string;
  readonly kind: TipoDeDescuento;
  readonly value: number;
  readonly scope: string;
  /** Para `sibling`: a partir de qué hijo se aplica. */
  readonly applies_from_nth: number | null;
  readonly active: boolean;
  /** Cuántas cuotas lo tienen **vigente hoy**. Lo de junio no cuenta. */
  readonly in_use: number;
  readonly created_at: Date | string;
}

export interface DescuentoAplicado {
  readonly id: string;
  readonly subscription_id: string;
  readonly rule_id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: TipoDeDescuento;
  readonly value: number;
  readonly starts_on: Date | string;
  /** `null` es **de por vida**, y es el caso por defecto. */
  readonly ends_on: Date | string | null;
  readonly notes: string | null;
  readonly created_by: string | null;
  readonly created_at: Date | string;
  /** Hoy está dentro de su rango de fechas. */
  readonly live: boolean;
}

export interface DescuentoQueCaduca {
  readonly id: string;
  readonly subscription_id: string;
  readonly student_id: string;
  readonly student_name: string;
  readonly payer_name: string | null;
  readonly code: string;
  readonly name: string;
  readonly ends_on: Date | string;
  readonly days_left: number;
  readonly current_cents: number;
  readonly next_cents: number;
  /** Lo que la familia va a notar. **Sin esta cifra la lista no sirve.** */
  readonly increase_cents: number;
}

export const AMBITOS: readonly {
  valor: AmbitoDeDescuento;
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    valor: 'manual',
    etiqueta: 'A mano',
    explicacion: 'Se aplica cuando el club lo decide, cuota por cuota.',
  },
  {
    valor: 'sibling',
    etiqueta: 'Hermanos',
    explicacion: 'Para el segundo hijo en adelante. Pide a partir de cuál.',
  },
  {
    valor: 'family',
    etiqueta: 'Familia',
    explicacion: 'Para todas las cuotas que paga el mismo pagador.',
  },
  {
    valor: 'annual_prepay',
    etiqueta: 'Pago anual',
    explicacion: 'Para quien paga el curso entero por adelantado.',
  },
  {
    valor: 'founder',
    etiqueta: 'Fundador',
    explicacion: 'Los primeros alumnos del club, con precio de por vida.',
  },
];

export const etiquetaDeAmbito = (scope: string): string =>
  AMBITOS.find((a) => a.valor === scope)?.etiqueta ?? scope;

/**
 * Cómo se lee el valor de una regla.
 *
 * Un `percent` y un `fixed` se pintan distinto **y con la misma cifra detrás**, que
 * es justo lo que hace fácil confundirlos: `value: 10` es 10 % o 10 €. Se pinta la
 * unidad siempre, nunca el número a secas.
 */
export const etiquetaDeValor = (
  kind: TipoDeDescuento,
  value: number
): string => {
  if (kind === 'percent') {
    // Sin decimales cuando es entero: «10 %», no «10,00 %».
    const n = new Intl.NumberFormat('es-ES', {
      maximumFractionDigits: 2,
    }).format(value);
    return `${n} %`;
  }

  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

/**
 * Lo que se teclea → lo que se manda.
 *
 * **No devuelve céntimos.** Devuelve el `numeric(7,2)` que la columna espera: un
 * porcentaje (`10`) o unos euros (`5.5`). Acepta coma y punto, porque un teclado
 * español escribe coma.
 *
 * Devuelve `null` cuando no se entiende, que es lo que el formulario enseña en vez
 * de mandar un `NaN` y comerse un `400`.
 */
export function aValorDeRegla(
  texto: string,
  kind: TipoDeDescuento
): number | null {
  const limpio = texto.trim().replace(',', '.');
  if (limpio === '' || !/^\d+(\.\d{1,2})?$/.test(limpio)) return null;

  const valor = Number.parseFloat(limpio);
  if (!Number.isFinite(valor) || valor <= 0) return null;

  // El esquema admite hasta 10000 para los dos, pero un porcentaje por encima de
  // 100 no es un descuento: es un error de tecleo que dejaría la cuota en negativo.
  if (kind === 'percent' && valor > 100) return null;
  if (valor > 10000) return null;

  return valor;
}

/** Valor → lo que se pone en el campo para editarlo. */
export const aTextoDeValor = (value: number): string =>
  String(value).replace('.', ',');

/**
 * Fin del curso: el 30 de junio del curso en marcha.
 *
 * Es la fecha que pone un club cuando le marca caducidad a una promoción, así que
 * es la que propone el formulario. De **julio en adelante** el curso que empieza
 * termina en junio del año siguiente, que es donde una cuenta ingenua se equivoca
 * de curso entero.
 */
export function finDeCurso(hoy = new Date()): string {
  const año = hoy.getMonth() >= 6 ? hoy.getFullYear() + 1 : hoy.getFullYear();
  return `${año}-06-30`;
}
